
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';
import { Conversation } from '@/types';
import { toast } from '@/hooks/use-toast';

/**
 * Fetches all conversations for a user
 */
export async function fetchConversationsService(userId: string): Promise<Conversation[]> {
  try {
    console.log("Fetching conversations for user:", userId);
    
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error("Error fetching conversations:", error);
      toast({
        title: "Erro",
        description: "Falha ao carregar suas conversas. Por favor, tente novamente.",
        variant: "destructive",
      });
      return [];
    }
    
    console.log("Fetched conversations:", data);
    return (data || []).map(conv => ({
      ...conv,
      metadata: conv.metadata as Conversation['metadata']
    }));
  } catch (error) {
    console.error("Exception in fetchConversations:", error);
    toast({
      title: "Erro",
      description: "Falha ao carregar suas conversas. Por favor, tente novamente.",
      variant: "destructive",
    });
    return [];
  }
}

/**
 * Creates a new conversation for a user with optional template
 */
export async function createConversationService(
  userId: string, 
  title: string = "Nova conversa",
  templateName: string = "lovable_gpt"
): Promise<Conversation | null> {
  try {
    const newConversationId = uuidv4();
    console.log("Creating new conversation with ID:", newConversationId, "template:", templateName);
    
    const { error } = await supabase
      .from('conversations')
      .insert({
        id: newConversationId,
        user_id: userId,
        title: title,
        created_at: new Date().toISOString(),
        metadata: { template: templateName }
      });

    if (error) {
      console.error("Error creating conversation:", error);
      toast({
        title: "Erro",
        description: "Falha ao criar nova conversa. Por favor, tente novamente.",
        variant: "destructive",
      });
      return null;
    }
    
    const newConversation: Conversation = {
      id: newConversationId,
      user_id: userId,
      title: title,
      last_message: null,
      created_at: new Date().toISOString(),
      metadata: { template: templateName }
    };
    
    return newConversation;
  } catch (error) {
    console.error("Exception in createNewConversation:", error);
    toast({
      title: "Erro",
      description: "Falha ao criar uma nova conversa. Por favor, tente novamente.",
      variant: "destructive",
    });
    return null;
  }
}

/**
 * Updates the template of a conversation
 */
export async function updateConversationTemplateService(
  conversationId: string, 
  templateName: string, 
  userId: string
): Promise<boolean> {
  try {
    // Fetch current metadata to preserve other keys
    const { data: currentData, error: fetchError } = await supabase
      .from('conversations')
      .select('metadata')
      .eq('id', conversationId)
      .eq('user_id', userId)
      .single();
    
    if (fetchError) {
      console.error("Error fetching conversation metadata:", fetchError);
      return false;
    }
    
    // Merge new template with existing metadata
    const existingMetadata = (currentData?.metadata as Record<string, any>) || {};
    const updatedMetadata = {
      ...existingMetadata,
      template: templateName
    };
    
    const { error } = await supabase
      .from('conversations')
      .update({ 
        metadata: updatedMetadata
      })
      .eq('id', conversationId)
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error updating conversation template:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error updating conversation template:", error);
    return false;
  }
}

/**
 * Updates the title of a conversation
 */
export async function updateConversationTitleService(conversationId: string, title: string, userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('conversations')
      .update({ title: title })
      .eq('id', conversationId)
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error updating conversation title:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error updating conversation title:", error);
    return false;
  }
}

/**
 * Updates the last message of a conversation
 */
export async function updateLastMessageService(conversationId: string, lastMessage: string, userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('conversations')
      .update({ last_message: lastMessage })
      .eq('id', conversationId)
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error updating last message:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error updating last message:", error);
    return false;
  }
}

/**
 * Deletes a conversation
 */
export async function deleteConversationService(conversationId: string, userId: string): Promise<boolean> {
  try {
    const { data: conversationData, error: fetchError } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .eq('user_id', userId)
      .single();
      
    if (fetchError) {
      console.error("Error fetching conversation for deletion:", fetchError);
      toast({
        title: "Erro",
        description: "Não foi possível verificar a propriedade dessa conversa.",
        variant: "destructive",
      });
      return false;
    }
    
    const { error: deleteError } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId);
      
    if (deleteError) {
      console.error("Error deleting conversation:", deleteError);
      toast({
        title: "Erro",
        description: "Falha ao excluir a conversa. Por favor, tente novamente.",
        variant: "destructive",
      });
      return false;
    }
    
    toast({
      title: "Sucesso",
      description: "Conversa excluída com sucesso.",
    });
    
    return true;
  } catch (error) {
    console.error("Exception in deleteConversation:", error);
    toast({
      title: "Erro",
      description: "Falha ao excluir a conversa. Por favor, tente novamente.",
      variant: "destructive",
    });
    return false;
  }
}

/**
 * Renames a conversation
 */
export async function renameConversationService(conversationId: string, newTitle: string, userId: string): Promise<boolean> {
  try {
    if (!newTitle.trim()) return false;
    
    console.log("Renaming conversation:", conversationId, "to:", newTitle);
    
    const { error } = await supabase
      .from('conversations')
      .update({ title: newTitle.trim() })
      .eq('id', conversationId)
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error renaming conversation:", error);
      toast({
        title: "Erro",
        description: "Falha ao renomear a conversa. Por favor, tente novamente.",
        variant: "destructive",
      });
      return false;
    }
    
    toast({
      title: "Sucesso",
      description: "Conversa renomeada com sucesso.",
    });
    
    return true;
  } catch (error) {
    console.error("Exception in renameConversation:", error);
    toast({
      title: "Erro",
      description: "Falha ao renomear a conversa. Por favor, tente novamente.",
      variant: "destructive",
    });
    return false;
  }
}
